import { defineStore } from 'pinia';
import { api } from '@/api/client';
import type { AuthResponse, User } from '@/api/types';
import * as AuthApi from '@/api/auth';
import { useAwardsStore } from '@/stores/awards';

const GOOGLE_STATE_KEY = 'lockin.google.state';

const TOKEN_KEY = 'lockin.token';

interface AuthState {
  token: string | null;
  user: User | null;
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    token: localStorage.getItem(TOKEN_KEY),
    user: null,
  }),
  getters: {
    isAuthenticated: (s) => !!s.token,
  },
  actions: {
    async login(email: string, password: string) {
      const resp = await api<AuthResponse>('POST', '/api/auth/login', { email, password });
      this.applyAuth(resp);
    },
    async register(payload: {
      email: string;
      password: string;
      firstname?: string;
      lastname?: string;
      nickname?: string;
      studentClass?: string;
    }) {
      const resp = await api<AuthResponse>('POST', '/api/auth/register', payload);
      this.applyAuth(resp);
    },
    async fetchMe() {
      if (!this.token) return;
      try {
        this.user = await api<User>('GET', '/api/auth/me');
      } catch {
        this.logout();
      }
    },
    async updateProfile(payload: Partial<User>) {
      this.user = await api<User>('PUT', '/api/profile', payload);
    },

    // ── passwordless OTP login ──
    requestOtp(email: string) {
      return AuthApi.requestOtp(email.trim());
    },
    async loginWithOtp(email: string, code: string) {
      const resp = await AuthApi.verifyOtp(email.trim(), code.trim());
      this.applyAuth(resp);
    },

    // ── password reset ──
    forgotPassword(email: string) {
      return AuthApi.forgotPassword(email.trim());
    },
    resetPassword(token: string, password: string) {
      return AuthApi.resetPassword(token, password);
    },

    // ── email verification ──
    async resendVerification() {
      await AuthApi.resendVerification();
    },
    async confirmVerification(token: string) {
      await AuthApi.confirmVerification(token);
      // Reflect the new state locally if we're the verified user.
      if (this.user) this.user = { ...this.user, emailVerified: true };
    },

    // ── Google sign-in (full-page redirect) ──
    async startGoogle() {
      const { authorizeUrl, state } = await AuthApi.startGoogle();
      sessionStorage.setItem(GOOGLE_STATE_KEY, state);
      window.location.assign(authorizeUrl);
    },
    async completeGoogle(code: string, state: string) {
      const expected = sessionStorage.getItem(GOOGLE_STATE_KEY);
      sessionStorage.removeItem(GOOGLE_STATE_KEY);
      if (!expected || expected !== state) {
        throw new Error('Ongeldige Google-inlogpoging. Probeer opnieuw.');
      }
      const resp = await AuthApi.completeGoogle(code, state);
      this.applyAuth(resp);
    },
    applyAuth(resp: AuthResponse) {
      this.token = resp.token;
      this.user = resp.user;
      localStorage.setItem(TOKEN_KEY, resp.token);
      // Comeback award (and any others queued at login) flows through
      // here so the overlay fires once the app shell is mounted.
      if (resp.awardsGranted?.length) useAwardsStore().enqueue(resp.awardsGranted);
    },
    logout() {
      this.token = null;
      this.user = null;
      localStorage.removeItem(TOKEN_KEY);
    },
  },
});
